import { useEffect } from "react";
import PropTypes from "prop-types";

/**
 * SEO
 */

const upsertMetaByName = (name, content) => {
  if (!content) return null;
  let tag = document.querySelector(`meta[name="${name}"]`);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  return created ? tag : null;
};

const upsertMetaByProperty = (property, content) => {
  if (!content) return null;
  let tag = document.querySelector(`meta[property="${property}"]`);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  return created ? tag : null;
};

const upsertLink = (rel, href) => {
  if (!href) return null;
  let tag = document.querySelector(`link[rel="${rel}"]`);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
  return created ? tag : null;
};

const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  robots,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType,
  siteName,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  structuredData,
}) => {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const createdTags = [
      upsertMetaByName("description", description),
      upsertMetaByName(
        "keywords",
        keywords && keywords.length ? keywords.join(", ") : "",
      ),
      upsertMetaByName("robots", robots),
      upsertMetaByProperty("og:title", ogTitle || title),
      upsertMetaByProperty("og:description", ogDescription || description),
      upsertMetaByProperty("og:image", ogImage),
      upsertMetaByProperty("og:url", ogUrl),
      upsertMetaByProperty("og:type", ogType),
      upsertMetaByProperty("og:site_name", siteName),
      upsertMetaByName("twitter:card", twitterCard),
      upsertMetaByName("twitter:title", twitterTitle || title),
      upsertMetaByName(
        "twitter:description",
        twitterDescription || description,
      ),
      upsertMetaByName("twitter:image", twitterImage || ogImage),
    ].filter(Boolean);

    const createdLink = upsertLink("canonical", canonicalUrl);
    if (createdLink) createdTags.push(createdLink);

    let structuredDataTag = null;
    if (structuredData) {
      structuredDataTag = document.createElement("script");
      structuredDataTag.type = "application/ld+json";
      structuredDataTag.text = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataTag);
    }

    return () => {
      document.title = previousTitle;
      createdTags.forEach((tag) => tag.remove());
      if (structuredDataTag) structuredDataTag.remove();
    };
  }, [
    title,
    description,
    keywords,
    canonicalUrl,
    robots,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    siteName,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    structuredData,
  ]);

  return null;
};

SEO.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  canonicalUrl: PropTypes.string,
  robots: PropTypes.string,
  ogTitle: PropTypes.string,
  ogDescription: PropTypes.string,
  ogImage: PropTypes.string,
  ogUrl: PropTypes.string,
  ogType: PropTypes.string,
  siteName: PropTypes.string,
  twitterCard: PropTypes.string,
  twitterTitle: PropTypes.string,
  twitterDescription: PropTypes.string,
  twitterImage: PropTypes.string,
  structuredData: PropTypes.object,
};

SEO.defaultProps = {
  description: "",
  keywords: [],
  canonicalUrl: "",
  robots: "index, follow",
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
  ogUrl: "",
  ogType: "website",
  siteName: "Funooni",
  twitterCard: "",
  twitterTitle: "",
  twitterDescription: "",
  twitterImage: "",
  structuredData: null,
};

export default SEO;
